import type { IApiConfig } from "@kuika/kuika-cl-model";

/**
 * The mail Provider dropdown holds a single composite value (`"{kind}|{id}"`) while the configuration stores
 * one id per provider kind. These two helpers are the only place that translates between the two, so App
 * Settings and the Config Manager cannot drift apart.
 */

/** Marks a `mailSettingsId` value as pointing at a Custom Mail Provider datasource instead of a settings record. */
export const CUSTOM_MAIL_PROVIDER_PREFIX = "custom";

/**
 * Builds the dropdown value from a stored configuration. A selected Custom Mail Provider wins over the SMTP /
 * AWS / Exchange ids: it replaces the app's mail provider entirely, so it is what the user must see selected.
 */
export function toMailSettingsIdValue(apiConfig: IApiConfig | undefined | null): string | null {
  if (!apiConfig) return null;

  if (apiConfig.customMailProviderDatasourceId) {
    return `${CUSTOM_MAIL_PROVIDER_PREFIX}|${apiConfig.customMailProviderDatasourceId}`;
  }
  if (apiConfig.smtpSettingsId) {
    return `smtp|${apiConfig.smtpSettingsId}`;
  }
  if (apiConfig.awsSettingsId) {
    return `aws|${apiConfig.awsSettingsId}`;
  }
  if (apiConfig.exchangeServerMailSettingsId) {
    return `exchangeServerMail|${apiConfig.exchangeServerMailSettingsId}`;
  }
  return null;
}

/**
 * Writes the dropdown value back onto the configuration being saved. `customMailProviderDatasourceId` is
 * always assigned — cleared when a platform provider is picked — because it takes precedence on load and a
 * leftover value would keep winning after the user switched away from it.
 */
export function applyMailSettingsIdToConfig(apiConfig: IApiConfig, mailSettingsId: string | null): void {
  const parts: string[] = (mailSettingsId ?? "").split("|");
  const kind: string = parts[0];
  const id: string | null = parts.length > 1 ? parts[1] : null;

  apiConfig.customMailProviderDatasourceId = kind === CUSTOM_MAIL_PROVIDER_PREFIX ? id : null;

  if (kind === "smtp") {
    apiConfig.smtpSettingsId = id;
  } else if (kind === "aws") {
    apiConfig.awsSettingsId = id;
  } else if (kind === "exchangeServerMail") {
    apiConfig.exchangeServerMailSettingsId = id;
  }
}
